import { GlobalInsuredAtomProperties } from "../../InsuredAtoms";
import { BaseTableColumn } from "../../../../dtos/base-table-column";
import { PolicyPaymentScheduleMultiTableChild } from "../../../../dtos/policy-payment-schedule-multi-table-child";
import { BaseTableChildrenData } from "../../../TrueUI/Tables/BaseTable2/TableProperties";
import { PolicyInvoiceBlob } from "../../../../dtos/policy-invoice-blob";
import { InvoiceDetailLineNumberEnum } from "../../../../dtos/invoice-detail-line-number-enum";

export const getChildrenData = (
  columns: BaseTableColumn[],
  childrenData: PolicyPaymentScheduleMultiTableChild[]
): BaseTableChildrenData[] =>
  childrenData.map(
    (children) =>
      ({
        groupId: children.id,
        columns: columns,
        data: children.rowData.map((row) => [
          "",
          "",
          row[0],
          row[1],
          row[2],
          row[3],
        ]),
      } as BaseTableChildrenData)
  ) ?? [];

export const getTotalEndorsementAmountFromInvoices = (
  invoices?: PolicyInvoiceBlob[] | null
) =>
  invoices?.reduce(
    (acc, invoiceJSON) => acc + (invoiceJSON.endorsementAmount ?? 0),
    0
  );

const getTotalInvoiceAmountFromInvoices = (
  invoices?: PolicyInvoiceBlob[] | null
) =>
  invoices?.reduce(
    (acc, invoiceJSON) =>
      acc +
      (invoiceJSON.invoiceTotal ?? 0) -
      (invoiceJSON.endorsementAmount ?? 0),
    0
  );

const getTotalInvoiceTotalsFromInvoices = (
  invoices?: PolicyInvoiceBlob[] | null
) =>
  invoices?.reduce(
    (acc, invoiceJSON) => acc + (invoiceJSON.invoiceTotal ?? 0),
    0
  );

export const validateEndorsementPremiumChangeBeforeBind = (
  atomValue: GlobalInsuredAtomProperties | null
) => {
  const policyJSON = atomValue?.policyQuoteInformation?.policyQuote;
  const totalEndorsementAmountFromInvoices =
    getTotalEndorsementAmountFromInvoices(policyJSON?.invoices);

  return (
    totalEndorsementAmountFromInvoices ===
    policyJSON?.bindInstructions?.endorsementPremiumChange
  );
};

export const calculateTotalsForFooters = (
  invoices?: PolicyInvoiceBlob[] | null
) => {
  const totalInvoiceAmount = getTotalInvoiceAmountFromInvoices(invoices) ?? 0;
  const totalEndorsement = getTotalEndorsementAmountFromInvoices(invoices) ?? 0;
  const totalPaymentDue = getTotalInvoiceTotalsFromInvoices(invoices) ?? 0;

  return { totalInvoiceAmount, totalEndorsement, totalPaymentDue };
};

export const updateInvoicesByEndorsementAmountChange = (
  endorsementAmount: number,
  uniqueKey: string,
  invoices?: PolicyInvoiceBlob[] | null
) =>
  invoices?.map((invoiceJSON) => {
    if (invoiceJSON.uniqueKey === uniqueKey) {
      const numberOfLocations = invoiceJSON.invoiceSubs?.length ?? 1;
      const endorsementAmountPerLocation =
        numberOfLocations !== 0
          ? endorsementAmount / numberOfLocations
          : endorsementAmount;

      const invoiceTotalMinusEndorsementPreviouslyApplied =
        invoiceJSON.invoiceTotal - (invoiceJSON.endorsementAmount ?? 0);
      const newInvoiceTotal =
        invoiceTotalMinusEndorsementPreviouslyApplied + endorsementAmount;
      return {
        ...invoiceJSON,
        endorsementAmount,
        invoiceTotal: newInvoiceTotal,
        invoiceSubs: invoiceJSON.invoiceSubs?.map((invoiceSubJSON) => {
          return {
            ...invoiceSubJSON,
            endorsementAmount: endorsementAmountPerLocation,
            invoiceDetails: invoiceSubJSON.invoiceDetails?.map(
              (invoiceDetailJSON) => {
                if (
                  invoiceDetailJSON.lineNumber ===
                  InvoiceDetailLineNumberEnum.PREMIUM
                ) {
                  const priceEachMinusEndorsementPreviouslyApplied =
                    invoiceDetailJSON.priceEach -
                    (invoiceSubJSON.endorsementAmount ?? 0);
                  const premiumValueUpdatedForInvoiceDetailPremium =
                    priceEachMinusEndorsementPreviouslyApplied +
                    endorsementAmountPerLocation;
                  return {
                    ...invoiceDetailJSON,
                    priceEach: premiumValueUpdatedForInvoiceDetailPremium,
                    extended: premiumValueUpdatedForInvoiceDetailPremium,
                  };
                }
                return invoiceDetailJSON;
              }
            ),
          };
        }),
      };
    }
    return invoiceJSON;
  });
